import { t } from "@lingui/core/macro";
import type { AnnotationShape } from "@rakazo/contracts";
import { type PointerEvent, useEffect, useRef, useState } from "react";
import {
  appendFreehandPoint,
  arrowHead,
  freehandPath,
  hitsShape,
  type MarkupTool,
  type RelPoint,
  shapeBounds,
  shapeFromDrag,
  shapeFromStroke,
  toPagePoint,
} from "../../lib/annotation-shapes";
import { isDrawCancelled, type PageDrawTask } from "../../lib/pdf-render";

/** One saved or drafted mark as the page draws it: number badge, shape and state. */
export type PageMark = {
  id: string;
  label: string;
  shape: AnnotationShape;
  done: boolean;
  active: boolean;
};

/**
 * Starts drawing one PDF page into a canvas at a CSS width. The promise resolves when the page is
 * on the canvas; cancel() stops the draw so the next one may use the canvas.
 */
export type DrawPage = (page: number, canvas: HTMLCanvasElement, cssWidth: number) => PageDrawTask;

/** Tool of the toolbar: one of the five shapes or "select" to pick existing marks. */
export type PageTool = MarkupTool | "select";

type Size = { width: number; height: number };

type Stroke = { start: RelPoint; points: readonly RelPoint[] };

/**
 * One page of the preview: the canvas with the rendered PDF, above it an SVG layer with the
 * marks. With a shape tool the reader drags a new mark; with "select" a click picks a mark. A
 * running draw is cancelled before the page draws again and when the page leaves.
 */
export function MarkupPage({
  page,
  size,
  marks,
  tool,
  color,
  drawPage,
  onCanvas,
  onDraw,
  onPick,
}: {
  page: number;
  size: Size;
  marks: readonly PageMark[];
  tool: PageTool;
  color: string;
  drawPage: DrawPage;
  onCanvas: (page: number, canvas: HTMLCanvasElement | null) => void;
  onDraw: (page: number, shape: AnnotationShape) => void;
  onPick: (id: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(typeof IntersectionObserver === "undefined");
  const [stroke, setStrokeState] = useState<Stroke | null>(null);
  const strokeRef = useRef<Stroke | null>(null);
  const setStroke = (next: Stroke | null) => {
    strokeRef.current = next;
    setStrokeState(next);
  };

  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !visible || size.width <= 0) return;
    let live = true;
    setFailed(false);
    // pdf.js draws into one canvas at a time: a new width cancels the draw still running.
    const task = drawPage(page, canvas, size.width);
    task.promise.then(
      () => {
        if (!live) return;
        setDrawn(true);
        onCanvas(page, canvas);
      },
      (error: unknown) => {
        if (live && !isDrawCancelled(error)) setFailed(true);
      },
    );
    return () => {
      live = false;
      task.cancel();
      onCanvas(page, null);
    };
  }, [drawPage, onCanvas, page, size.width, visible]);

  const pointAt = (event: PointerEvent<SVGSVGElement>): RelPoint =>
    toPagePoint(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect());

  const onPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    const point = pointAt(event);
    if (tool === "select") {
      const hit = [...marks].reverse().find((m) => hitsShape(m.shape, point, size));
      if (hit) onPick(hit.id);
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setStroke({ start: point, points: [point] });
  };

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const current = strokeRef.current;
    if (!current) return;
    const point = pointAt(event);
    setStroke({
      start: current.start,
      points:
        tool === "freehand"
          ? appendFreehandPoint(current.points, point, size)
          : [current.start, point],
    });
  };

  const finish = (event: PointerEvent<SVGSVGElement>) => {
    const current = strokeRef.current;
    if (!current || tool === "select") return;
    const end = pointAt(event);
    const shape =
      tool === "freehand"
        ? shapeFromStroke(appendFreehandPoint(current.points, end, size), color, size)
        : shapeFromDrag(tool, current.start, end, color, size);
    setStroke(null);
    if (shape) onDraw(page, shape);
  };

  const preview = stroke && tool !== "select" ? strokeShape(tool, stroke, color) : null;

  return (
    <div
      ref={boxRef}
      className="relative mx-auto bg-white shadow-[0_1px_3px_rgba(0,0,0,.12),0_8px_24px_rgba(0,0,0,.06)]"
      style={{ width: size.width, height: size.height }}
      data-page={page}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full"
        aria-label={t`Page ${page}`}
        role="img"
      />
      {!drawn && !failed ? (
        <div className="absolute inset-0 animate-pulse bg-muted/40" aria-hidden="true" />
      ) : null}
      {failed ? (
        <p className="absolute inset-x-0 top-1/2 text-center text-[13px] text-muted-foreground">
          {t`This page could not be drawn.`}
        </p>
      ) : null}
      <svg
        className={`absolute inset-0 h-full w-full touch-none ${tool === "select" ? "cursor-default" : "cursor-crosshair"}`}
        viewBox={`0 0 ${size.width} ${size.height}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finish}
        onPointerCancel={() => setStroke(null)}
        aria-hidden="true"
        data-testid={`markup-layer-${page}`}
      >
        {marks.map((m) => (
          <ShapeView key={m.id} shape={m.shape} size={size} done={m.done} active={m.active} />
        ))}
        {preview ? <ShapeView shape={preview} size={size} done={false} active /> : null}
      </svg>
      {marks.map((m) => (
        <MarkBadge key={m.id} mark={m} size={size} />
      ))}
    </div>
  );
}

function strokeShape(tool: MarkupTool, stroke: Stroke, color: string): AnnotationShape | null {
  if (tool === "freehand") {
    return stroke.points.length >= 2
      ? { kind: "freehand", points: stroke.points.map((p) => [p[0], p[1]]), color }
      : null;
  }
  const end = stroke.points.at(-1) ?? stroke.start;
  return { kind: tool, points: [stroke.start, end], color } as AnnotationShape;
}

function ShapeView({
  shape,
  size,
  done,
  active,
}: {
  shape: AnnotationShape;
  size: Size;
  done: boolean;
  active: boolean;
}) {
  const stroke = done ? "#9a9890" : shape.color;
  const width = active ? 3 : 2.25;
  const dash = done ? "8 6" : undefined;
  const px = (p: RelPoint): [number, number] => [p[0] * size.width, p[1] * size.height];
  if (shape.kind === "freehand") {
    return (
      <path
        d={freehandPath(shape.points, size)}
        fill="none"
        stroke={stroke}
        strokeWidth={width}
        strokeDasharray={dash}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  }
  if (shape.kind === "arrow") {
    const tail = (shape.points[0] ?? [0, 0]) as RelPoint;
    const head = (shape.points[1] ?? tail) as RelPoint;
    const [left, right] = arrowHead(tail, head, size);
    const [tx, ty] = px(tail);
    const [hx, hy] = px(head);
    const [lx, ly] = px(left);
    const [rx, ry] = px(right);
    return (
      <g stroke={stroke} strokeWidth={width} strokeLinecap="round" fill="none">
        <line x1={tx} y1={ty} x2={hx} y2={hy} strokeDasharray={dash} />
        <polyline points={`${lx},${ly} ${hx},${hy} ${rx},${ry}`} strokeLinejoin="round" />
      </g>
    );
  }
  const b = shapeBounds(shape);
  const x = b.x0 * size.width;
  const y = b.y0 * size.height;
  const w = (b.x1 - b.x0) * size.width;
  const h = (b.y1 - b.y0) * size.height;
  if (shape.kind === "highlight") {
    return (
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill={done ? "#9a9890" : shape.color}
        opacity={done ? 0.25 : 0.4}
        style={{ mixBlendMode: "multiply" }}
      />
    );
  }
  if (shape.kind === "ellipse") {
    return (
      <ellipse
        cx={x + w / 2}
        cy={y + h / 2}
        rx={w / 2}
        ry={h / 2}
        fill="none"
        stroke={stroke}
        strokeWidth={width}
        strokeDasharray={dash}
      />
    );
  }
  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx={4}
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeDasharray={dash}
    />
  );
}

function MarkBadge({ mark, size }: { mark: PageMark; size: Size }) {
  const b = shapeBounds(mark.shape);
  const left = Math.min(size.width - 20, Math.max(0, b.x1 * size.width - 10));
  const top = Math.max(0, b.y0 * size.height - 10);
  return (
    <span
      className={`pointer-events-none absolute grid size-5 place-items-center rounded-full text-[11px] font-semibold tabular-nums text-white ${mark.done ? "bg-[#30a46c]" : "bg-foreground"} ${mark.active ? "ring-2 ring-white ring-offset-1 ring-offset-foreground" : ""}`}
      style={{ left, top }}
      aria-hidden="true"
    >
      {mark.done ? "✓" : mark.label}
    </span>
  );
}
